import { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import type { GuidePlacement, GuideTopic } from '../engine/types';
import {
  placePopover,
  POPOVER_COLLISION_PADDING,
  POPOVER_SIDE_OFFSET,
  type Size,
} from '../utils/popoverPlacement';
import { GuideTopicBody } from './GuideTopicBody';

// Older Safari and Chrome WebViews reject dvh, and one invalid unit drops the
// whole max-height.
const VIEWPORT_HEIGHT = CSS.supports('height', '100dvh') ? '100dvh' : '100vh';

interface GuideStepPopoverProps {
  topic: GuideTopic;
  rect: DOMRect | null;
  placement: GuidePlacement;
  stepNumber: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onDisableAll: () => void;
}

export function GuideStepPopover({
  topic,
  rect,
  placement,
  stepNumber,
  totalSteps,
  isFirst,
  isLast,
  onNext,
  onPrev,
  onSkip,
  onDisableAll,
}: GuideStepPopoverProps) {
  const [contentRef, size] = useElementSize();

  if (!rect) return null;

  // Until the popover has been measured, Radix places it as before; the
  // measurement lands right after the first layout and corrects it.
  const { anchor, side } = size
    ? placePopover(rect, placement, size)
    : { anchor: rect, side: placement };

  return (
    <Popover open modal>
      <PopoverAnchor asChild>
        <div
          className="pointer-events-none fixed"
          style={{
            left: anchor.left,
            top: anchor.top,
            width: anchor.width,
            height: anchor.height,
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        side={side}
        role="dialog"
        aria-label="Guía de esta pantalla"
        className="z-[60] flex w-80 flex-col"
        // A phone on its side is shorter than a long step. Capping the height
        // keeps the whole card on screen; only the text scrolls, so the
        // buttons never leave it.
        style={{
          maxHeight: `calc(${VIEWPORT_HEIGHT} - ${2 * POPOVER_COLLISION_PADDING}px)`,
        }}
        sideOffset={POPOVER_SIDE_OFFSET}
        collisionPadding={POPOVER_COLLISION_PADDING}
        // The anchor moves through React state on scroll, which renders after
        // floating-ui has handled that scroll. Its own move detection misses
        // some of those moves and ignores a zero-height anchor, like the
        // fallback's, entirely. Re-placing every frame keeps the popover on
        // the anchor; it only re-renders when the position changes.
        updatePositionStrategy="always"
        // Radix focuses the first button, "Saltear", and on a fresh page load
        // the browser rings it as if skipping were the suggested action. The
        // card takes focus instead; Tab still reaches every button.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus({ preventScroll: true });
        }}
        // Radix only wraps Tab around from the first or the last button, and
        // the card is neither, so Shift+Tab from it went nowhere.
        onKeyDown={(event) => {
          if (event.key !== 'Tab' || !event.shiftKey) return;
          if (event.target !== event.currentTarget) return;
          const buttons = event.currentTarget.querySelectorAll('button');
          if (buttons.length === 0) return;
          event.preventDefault();
          buttons[buttons.length - 1].focus();
        }}
        onEscapeKeyDown={onSkip}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="min-h-0 overflow-y-auto">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            Paso {stepNumber} de {totalSteps}
          </p>
          <GuideTopicBody topic={topic} />
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={onSkip}>
            Saltear
          </Button>
          <div className="flex gap-2">
            {!isFirst && (
              <Button variant="outline" size="sm" onClick={onPrev}>
                Anterior
              </Button>
            )}
            <Button size="sm" onClick={onNext}>
              {isLast ? 'Listo' : 'Siguiente'}
            </Button>
          </div>
        </div>

        <button
          type="button"
          onClick={onDisableAll}
          className="mt-3 w-full text-center text-xs text-muted-foreground underline hover:text-gray-700"
        >
          No mostrar más las guías
        </button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Layout size of the element the returned ref is attached to, kept current
 * while its content changes from step to step.
 *
 * Reads offset sizes rather than the observer's content rect, which leaves out
 * the padding and border, and those take room on screen too.
 */
function useElementSize() {
  const [size, setSize] = useState<Size | null>(null);
  const [observer] = useState(
    () =>
      new ResizeObserver(([entry]) => {
        const element = entry.target as HTMLElement;
        // A popover hidden while its anchor is missing reports 0×0 on its way
        // out, and keeping that would place the next one for an empty card.
        if (!element.isConnected) return;
        const width = element.offsetWidth;
        const height = element.offsetHeight;
        setSize((prev) =>
          prev && prev.width === width && prev.height === height
            ? prev
            : { width, height },
        );
      }),
  );
  const observed = useRef<HTMLElement | null>(null);

  useEffect(
    () => () => {
      observer.disconnect();
      observed.current = null;
    },
    [observer],
  );

  // Radix composes this into a ref it rebuilds on every render, so React
  // calls it with null and then the same element after each commit.
  // Re-observing on each would queue a new measurement every scroll frame.
  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || element === observed.current) return;
      observer.disconnect();
      observer.observe(element);
      observed.current = element;
    },
    [observer],
  );

  return [ref, size] as const;
}
